import { getNamespaceIdFromState } from './selectors'
import namespacePaths from './namespacePaths'

export function isAProductPath(path) {
  if (typeof path !== 'string') {
    return false
  }

  return path.includes('/product/')
}

export function isANamespacePath(path) {
  const isNamespacePath = namespacePaths.some(namespacePath =>
    path?.includes(namespacePath),
  )

  return isNamespacePath
}

export function isDesiredContentUpdate(entry, entryId, desiredEntryId) {
  return !entry || !entryId || entryId === desiredEntryId
}

export function verifyRouteIsOnEntry(entry, routeSlug) {
  const { routes = [], pageData } = entry
  const { routes: pageDataRoutes = [], route } = pageData || {}
  const isRoute = route === routeSlug
  // TODO: remove this isOldRoutes and pageDataRoutes
  const isOldRoutes = routes.includes(routeSlug)
  const isPageDataRoutes = pageDataRoutes.includes(routeSlug)

  const onAnyRouteOptions = isRoute || isOldRoutes || isPageDataRoutes
  return onAnyRouteOptions
}

export function verifyWeAreOnProperPath(pathname, blocklist) {
  const isABlocklistedPath = isOnPathBlocklist(pathname, blocklist)
  const isNamespacePath = isAProductPath(pathname) || isANamespacePath(pathname)

  if (
    pathname === undefined ||
    pathname === null ||
    !isNamespacePath ||
    isABlocklistedPath
  ) {
    return false
  }

  return true
}

export function verifyIfDesiredContentUpdate(
  appState,
  entry,
  actionEntryId,
  desiredNamespace,
) {
  const parentEntryId = getNamespaceIdFromState(appState, desiredNamespace)
  if (!parentEntryId) {
    return false
  }

  const isParentEntryId = isDesiredContentUpdate(
    entry,
    actionEntryId,
    parentEntryId,
  )
  if (!isParentEntryId) {
    return false
  }

  return true
}

export function verifyThatStartTreeIsNew(pathname, startPath) {
  if (!startPath && pathname) {
    return true
  }

  if (startPath && pathname) {
    if (startPath !== pathname) {
      return true
    }
  }

  return false
}

export function isLastRoute(path, pathLevel) {
  let modifiedPath = path
  if (path && path.length && path.length > 0) {
    const lastCharacterIndex = path.length - 1
    const lastCharacter = path[lastCharacterIndex]

    if (lastCharacter === '/') {
      modifiedPath = modifiedPath.slice(0, modifiedPath.length - 1)
    }

    if (modifiedPath.length > 0) {
      const pathSlugs = modifiedPath.split('/')
      const isRouteLastSlug = pathSlugs.length === pathLevel + 1

      return isRouteLastSlug
    }
  }

  return false
}

export function isRouteInPath(path, routeSlug) {
  const pathSlugs = path.split('/')
  const pathIndex = pathSlugs.indexOf(routeSlug)
  const inPath = pathIndex !== -1

  return inPath
}

export const isOnPathBlocklist = (path, blocklist) => {
  return blocklist.some(blocklistedPath => {
    // Allows for wildcard matching
    if (blocklistedPath.endsWith('*')) {
      return path.startsWith(blocklistedPath.slice(0, -1))
    }

    // Exact match
    return path === blocklistedPath
  })
}

/**
 * Central list of routes that should be blocklisted from content node sagas
 * These routes use PagesV2Container with content-np service or have special handling
 * Used by both getBlocklist (for content node sagas) and isProgramFlexPathname (for GetContentfulResource calls)
 * Note: family-resources is included for content node saga blocklisting, but isProgramFlexPathname filters it out
 * because some family-resources routes (like static routes using PagesContainer) still need GetContentfulResource calls
 */
export const CONTENT_NODE_BLOCKLIST_BASE_PATHS = [
  '/product/middle-school',
  '/product/elementary',
  '/product/high-school',
  '/family-resources',
]

/**
 * Core function to check if a pathname is a program flex route (recursive - matches any depth)
 * This is the shared logic used by all program flex route checkers
 * @param {string} pathname - The pathname to check
 * @returns {boolean} - True if the pathname is a program flex route
 */
export function isProgramFlexPathname(pathname) {
  if (!pathname) {
    return false
  }

  // Normalize pathname - remove any query strings or hashes, ensure it starts with /
  let normalizedPathname = pathname.split('?')[0].split('#')[0]
  if (!normalizedPathname.startsWith('/')) {
    normalizedPathname = '/' + normalizedPathname
  }

  // Recursive check - matches any path that starts with these base paths, regardless of depth
  // This ensures /product/elementary/page1/page2/page3/etc. all get blocked
  // Uses central CONTENT_NODE_BLOCKLIST_BASE_PATHS list, but filters out /family-resources
  // because family-resources routes may still need GetContentfulResource calls
  // Check if pathname starts with any of the program flex base paths
  // This works recursively for any depth of nesting
  // We check both with and without trailing slash to be safe
  return CONTENT_NODE_BLOCKLIST_BASE_PATHS.filter(
    basePath => basePath !== '/family-resources',
  ).some(basePath => {
    // Match exact path or path with trailing slash or path with sub-routes
    return (
      normalizedPathname === basePath ||
      normalizedPathname.startsWith(basePath + '/') ||
      normalizedPathname.startsWith(basePath + '?') ||
      normalizedPathname.startsWith(basePath + '#')
    )
  })
}
